import { useEffect, useState } from "react";
import { Quote, Star, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { ConsultButton, WhatsAppLink } from "@/components/CtaButtons";
import { useLanguage } from "@/i18n/LanguageContext";
import { SERVICE_AREAS } from "@/data/content";
import { IMAGES } from "@/data/images";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

export const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <section className="bg-ink py-24 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal><SectionHeading dark center eyebrow={t("testimonials.eyebrow")} title={t("testimonials.title")} /></Reveal>
        {items && items.length > 0 ? (
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {items.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.08}>
                <figure data-testid={`testimonial-card-${i}`} className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-8">
                  <div className="flex gap-1" aria-label={`${item.rating} out of 5 stars`}>
                    {Array.from({ length: item.rating }).map((_, s) => (
                      <Star key={s} className="h-4 w-4 fill-gold text-gold" />
                    ))}
                  </div>
                  <blockquote className="mt-5 text-sm leading-relaxed text-white/75">“{item.text}”</blockquote>
                  <figcaption className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-gold">
                    {item.name}{item.location ? ` · ${item.location}` : ""}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal delay={0.1}>
            <div className="mx-auto mt-14 max-w-xl rounded-3xl border border-dashed border-white/15 bg-white/[0.03] p-12 text-center" data-testid="testimonials-placeholder">
              <Quote className="mx-auto h-8 w-8 text-gold/60" />
              <p className="mt-5 text-sm leading-relaxed text-white/60">{t("testimonials.pending")}</p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <button type="button" disabled title={t("footer.socialSoon")} data-testid="leave-review-button"
                  className="cursor-not-allowed rounded-full border border-white/15 px-6 py-2.5 text-xs font-bold text-white/40">
                  {t("testimonials.leaveReview")}
                </button>
                <button type="button" disabled title={t("footer.socialSoon")} data-testid="view-google-reviews-button"
                  className="cursor-not-allowed rounded-full border border-white/15 px-6 py-2.5 text-xs font-bold text-white/40">
                  {t("testimonials.viewGoogle")}
                </button>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export const FaqSection = () => {
  const { t } = useLanguage();
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  return (
    <section className="bg-white py-24 md:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <Reveal><SectionHeading center eyebrow={t("faq.eyebrow")} title={t("faq.title")} /></Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {faqs.map((faq, i) => (
              <AccordionItem key={faq.id} value={faq.id} className="border-charcoal/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg text-charcoal hover:text-gold-dark hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-charcoal/65">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export const ServiceAreasSection = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-offwhite py-24 md:py-32" data-testid="service-areas">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal><SectionHeading eyebrow={t("areas.eyebrow")} title={t("areas.title")} /></Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {SERVICE_AREAS.map((area, i) => (
            <Reveal key={area.name} delay={i * 0.08}>
              <div data-testid={`area-card-${i}`} className="h-full rounded-2xl border border-charcoal/10 bg-white p-8 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold-dark/40">
                <MapPin className="h-6 w-6 text-gold-dark" />
                <h3 className="mt-5 font-display text-2xl text-charcoal">{area.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{area.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-charcoal/60">
            {t("areas.notListed")}{" "}
            <Link to="/contact" data-testid="areas-contact-link" className="font-bold text-gold-dark underline-offset-4 hover:underline">
              {t("nav.contact")}
            </Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export const FinalCta = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden bg-ink py-28 md:py-40" data-testid="final-cta">
      <img src={IMAGES.hero2} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/60 to-ink" />
      <div className="relative mx-auto max-w-3xl px-5 text-center md:px-8">
        <Reveal>
          <p className="font-display text-lg italic text-gold">{t("footer.tagline")}</p>
          <h2 className="mt-5 font-display text-4xl leading-tight tracking-tight text-offwhite md:text-6xl">
            {t("finalCta.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/70">{t("finalCta.sub")}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <ConsultButton testid="final-consult-button" />
            <WhatsAppLink testid="final-whatsapp-button" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
